import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SUPPLIERS = ['Booker', 'Bestway', 'Parfetts', 'Rayburn'];
const NAV_ITEMS = [
  { label:'Home', href:'/', icon:'home' },
  { label:'Compare', href:'/compare', icon:'compare' },
  { label:'Scan', href:'/scan', icon:'scan' },
  { label:'Basket', href:'/basket', icon:'basket' },
  { label:'Insights', href:'/insights', icon:'insights' }
];

function NavIcon({ name }) {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === 'home' && <><path d="M4 11 12 4l8 7"/><path d="M6 10v9h12v-9"/></>}
    {name === 'compare' && <><path d="M4 7h16M4 17h16"/><path d="m8 4-4 3 4 3M16 14l4 3-4 3"/></>}
    {name === 'scan' && <><path d="M8 3H5a2 2 0 0 0-2 2v3m13-5h3a2 2 0 0 1 2 2v3M3 16v3a2 2 0 0 0 2 2h3m13-5v3a2 2 0 0 1-2 2h-3M5 12h14"/></>}
    {name === 'basket' && <><path d="M4 8h16l-1.3 10.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8L4 8z"/><path d="M8.5 8 11 3.5M15.5 8 13 3.5"/></>}
    {name === 'insights' && <><path d="M4 20V12m5 8V8m5 12V4m5 16v-7"/><path d="M2 20h20"/></>}
  </svg>;
}

function money(value) {
  return value == null ? null : `£${Number(value).toFixed(2)}`;
}

export default function SupplierBaskets() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const [readStatus, setReadStatus] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    fetch('/api/account/me', { cache:'no-store' }).then(async response => {
      if (response.status === 401) { navigate('/login?returnUrl=/basket', { replace:true }); return; }
      if (!response.ok) throw new Error('Could not load your shop.');
      const account = await response.json();
      if (!active) return;
      setShops(account.shops || []);
      setShopId(account.shops?.[0]?.shopId || '');
    }).catch(cause => { if (active) setError(cause.message || 'Could not load your shop.'); })
      .finally(() => { if (active) setAccountLoading(false); });
    return () => { active = false; };
  }, [navigate]);

  useEffect(() => {
    if (!shopId) return undefined;
    let active = true;
    const wait = ms => new Promise(resolve => window.setTimeout(resolve, ms));
    async function read() {
      setLoading(true); setError(''); setSnapshot(null); setReadStatus('Sending request to shop driver…'); setElapsed(0);
      try {
        const response = await fetch('/api/baskets/read', {
          method:'POST', headers:{ 'Content-Type':'application/json', 'X-ShopkeeperIQ-Client':'1' },
          body:JSON.stringify({ shopId })
        });
        if (response.status === 401) { navigate('/login?returnUrl=/basket', { replace:true }); return; }
        const job = await response.json();
        if (!response.ok) throw new Error(job.detail || job.title || 'Could not start the basket read.');
        for (let attempt = 0; attempt < 120 && active; attempt += 1) {
          if (attempt > 0) await wait(1000);
          if (!active) return;
          const poll = await fetch(`/api/jobs/${encodeURIComponent(job.id)}`, { cache:'no-store' });
          if (poll.status === 401) { navigate('/login?returnUrl=/basket', { replace:true }); return; }
          if (!poll.ok) continue;
          const current = await poll.json();
          if (!active) return;
          setReadStatus(current.status === 'running' ? 'Shop driver is reading supplier baskets…' : 'Waiting for the shop driver…');
          if (current.status === 'completed') {
            if (!current.result?.suppliers) throw new Error('The driver returned an unreadable basket response.');
            setSnapshot(current.result);
            return;
          }
          if (current.status === 'failed' || current.status === 'expired')
            throw new Error(current.message || 'The driver could not read supplier baskets.');
        }
        if (active) throw new Error('The basket read timed out.');
      } catch (cause) { if (active) setError(cause.message || 'Could not read supplier baskets.'); }
      finally { if (active) setLoading(false); }
    }
    read();
    return () => { active = false; };
  }, [shopId, refreshKey, navigate]);

  useEffect(() => {
    if (!loading) return undefined;
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [loading]);

  const bySupplier = Object.fromEntries((snapshot?.suppliers || []).map(result => [result.supplier, result]));
  return (
    <main style={{ minHeight:'100dvh', background:'#F4F6FB', padding:'22px 16px 82px', fontFamily:'Inter,sans-serif', color:'#0D1B2A' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
        <div><h1 style={{ margin:0, fontFamily:'Poppins,sans-serif', fontSize:23 }}>Supplier baskets</h1>
          <p style={{ margin:'4px 0 0', fontSize:12, color:'#6B7894' }}>Live items from your shop computer</p></div>
        <button onClick={() => setRefreshKey(value => value + 1)} disabled={!shopId || loading}
          style={{ border:0, borderRadius:10, padding:'10px 14px', background:'#1B4DE4', color:'#fff', fontWeight:700 }}>
          {loading ? 'Reading…' : 'Refresh'}
        </button>
      </div>
      {shops.length > 1 && <select value={shopId} onChange={event => setShopId(event.target.value)}
        aria-label="Shop" style={{ width:'100%', marginTop:16, padding:10, borderRadius:10, border:'1px solid #D6DEEA' }}>
        {shops.map(shop => <option key={shop.shopId} value={shop.shopId}>{shop.name}</option>)}
      </select>}
      {accountLoading && <p role="status" style={{ marginTop:20, color:'#6B7894' }}>Connecting to your shop…</p>}
      {!accountLoading && !shopId && !error && <p style={{ marginTop:20 }}>Set up a shop in Settings to read supplier baskets.</p>}
      {loading && <p role="status" style={{ color:'#6B7894', fontSize:12, marginTop:18 }}>{readStatus} · {elapsed}s{elapsed >= 10 ? ' · Waiting for all suppliers to finish. A basket action already in progress can delay the read.' : ''}</p>}
      {error && <p role="alert" style={{ color:'#B42318', marginTop:20 }}>{error}</p>}
      {snapshot?.fetchedAt && <p style={{ color:'#6B7894', fontSize:12, margin:'18px 0 8px' }}>
        Updated {new Date(snapshot.fetchedAt).toLocaleString('en-GB')}
      </p>}
      <div style={{ display:'grid', gap:14, marginTop:16 }}>
        {SUPPLIERS.map(supplier => {
          const basket = bySupplier[supplier];
          return <section key={supplier} style={{ background:'#fff', border:'1px solid #E3E8F1', borderRadius:16, padding:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h2 style={{ margin:0, fontSize:17 }}>{supplier}</h2>
              {basket?.status === 'ok' && <span style={{ color:'#0F9E57', fontSize:12 }}>{basket.items.length} lines</span>}
            </div>
            {loading && !basket && <p style={{ color:'#6B7894', fontSize:13 }}>Reading basket…</p>}
            {snapshot && !basket && <p role="status" style={{ color:'#9A6717', fontSize:13 }}>The driver returned no result for this supplier. Check that the shop driver is up to date, then refresh.</p>}
            {basket?.status === 'empty' && <p style={{ color:'#6B7894', fontSize:13 }}>Basket is empty.</p>}
            {basket && !['ok', 'empty'].includes(basket.status) && <p role="status" style={{ color:'#9A6717', fontSize:13 }}>
              {basket.message || 'Could not read this supplier basket.'}
            </p>}
            {['ok', 'partial'].includes(basket?.status) && <div style={{ marginTop:12, display:'grid', gap:9 }}>
              {basket.items.map((item, index) => <div key={`${item.sku || item.barcode || item.name}-${index}`}
                style={{ borderTop:'1px solid #EFF1F6', paddingTop:9, display:'flex', justifyContent:'space-between', gap:12 }}>
                <div><strong style={{ fontSize:13 }}>{item.name}</strong>
                  {(item.packSize || item.sku || item.barcode) && <div style={{ fontSize:11, color:'#8A97AD', marginTop:3 }}>
                    {[item.packSize, item.sku, item.barcode].filter(Boolean).join(' · ')}
                  </div>}
                </div>
                <div style={{ textAlign:'right', fontSize:12, whiteSpace:'nowrap' }}>
                  <div>×{item.quantity}</div>
                  {money(item.lineTotalExVat) && <div>{money(item.lineTotalExVat)}</div>}
                </div>
              </div>)}
            </div>}
          </section>;
        })}
      </div>
      <nav aria-label="Bottom navigation" style={{ position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)', width:'min(100%, 480px)', height:62,
        background:'#fff', borderTop:'1px solid #EAEEF6', display:'flex', justifyContent:'space-around', alignItems:'flex-start', padding:'8px 6px 0', zIndex:10 }}>
        {NAV_ITEMS.map(item => <a key={item.href} href={item.href} aria-current={item.href === '/basket' ? 'page' : undefined}
          style={{ textDecoration:'none', display:'flex', flexDirection:'column', alignItems:'center', gap:3,
            color:item.href === '/basket' ? '#1B4DE4' : '#9AA6BC', width:56 }}>
          <NavIcon name={item.icon}/><span style={{ fontSize:9.5, fontWeight:item.href === '/basket' ? 700 : 600 }}>{item.label}</span>
        </a>)}
      </nav>
    </main>
  );
}
